import type { ReactNode } from "react";
import {
  Children,
  isValidElement,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import type {
  AnyRouteProps,
  CurrentUrlReturn,
  NormalizedRoutes,
  RouteComponent,
  RouteDescriptor,
  RouteNode,
  UrlParams,
} from "./types";
import { rawRoutes } from "./routes";

type QueryToken = { key: string; token: string; defaultValue?: string };
type CompiledPattern = {
  pattern: string;
  matcher: URLPattern;
  searchTokens: QueryToken[];
  // Literal query segments (`k=v` rather than `k=:t`); the URL must carry them
  // for the pattern to match.
  searchLiterals: Array<[string, string]>;
};

export const useCurrentUrl = <T extends RouteNode>(
  scope: T,
  where?: Partial<CurrentUrlReturn<T>>
): CurrentUrlReturn<T> => {
  const patterns = collectPatterns(scope);
  const location = useLocationState();
  let match = findMatch(patterns, location);
  const lastMatchRef = useRef<Record<string, string> | null>(null);

  if (
    match &&
    where &&
    !Object.entries(where).every(([k, v]) => match![k] === v)
  ) {
    match = null;
  }

  // If the URL no longer matches (or matches a different `where`), return last
  // known values to avoid crashes during concurrent rendering before Route
  // unmounts (see #969), and so a covered modal keeps its params.
  if (!match) {
    return (lastMatchRef.current ?? {}) as CurrentUrlReturn<T>;
  }

  lastMatchRef.current = match;

  // Apply defaults from the first pattern (the scope itself)
  if (patterns.length > 0 && patterns[0]) {
    const compiled = patterns[0];
    const defaults = new Map<string, string>();
    for (const { token, defaultValue } of compiled.searchTokens) {
      if (defaultValue !== undefined && !(token in match)) {
        defaults.set(token, defaultValue);
      }
    }

    // Check if URL contains default values and normalize if needed. A pattern
    // with literal segments (a oneOf case) is page-agnostic, so it never
    // rewrites the URL — it doesn't know the page.
    const hasDefaults = compiled.searchTokens.some(
      ({ token, defaultValue }) =>
        defaultValue && match![token] === defaultValue
    );
    if (hasDefaults && compiled.searchLiterals.length === 0) {
      const normalizedUrl = buildUrl(normalizeParams(match), compiled);
      if (normalizedUrl !== location) {
        history.replaceState(history.state, "", normalizedUrl);
      }
    }

    return { ...Object.fromEntries(defaults), ...match } as CurrentUrlReturn<T>;
  }

  return match as CurrentUrlReturn<T>;
};

export const makeUrl = <T extends RouteNode>(
  scope: T | null,
  params?: UrlParams<T> | Record<string, string | number>
): string => {
  // A oneOf case is page-agnostic and addressed absolutely: its params
  // translate to the bracket form, any of its params not given are cleared,
  // and the URL is built from whatever page is current.
  if (scope && isCaseNode(scope)) {
    const namespaced: Record<string, string> = {
      [scope.discriminant]: scope.caseName,
    };
    const search = new URLSearchParams(currentLocationParts().search);
    for (const key of search.keys()) {
      if (key.startsWith(`${scope.discriminant}[`)) namespaced[key] = "";
    }
    for (const [k, v] of Object.entries(normalizeParams(params))) {
      namespaced[`${scope.discriminant}[${k}]`] = v;
    }
    return makeUrl(null, namespaced);
  }
  const compiled = scope
    ? compilePattern(scope.pattern)
    : currentCompiledPattern();
  const ownTokenKeys = scope ? extractOwnTokenKeys(scope) : null;
  const related = scope ? collectPatterns(scope) : [compiled];
  const mergedParams = {
    ...getCurrentParams(compiled, ownTokenKeys, related),
    ...normalizeParams(params),
  };
  return buildUrl(mergedParams, compiled);
};

function extractOwnTokenKeys(scope: RouteNode): Set<string> {
  if (!scope.ownTail) return new Set();
  const { search } = splitUrl(`/${scope.ownTail}`);
  return new Set(extractTokens(search).map(({ key }) => key));
}

function currentLocationParts() {
  if (typeof location === "undefined") {
    return { pathname: "/", search: "", hash: "" };
  }
  return location;
}

function currentCompiledPattern(): CompiledPattern {
  const current = currentLocationParts();
  const currentLocation = `${current.pathname}${current.search}${current.hash}`;
  const target = splitUrl(currentLocation);
  for (const root of appRoutes) {
    for (const compiled of collectPatterns(root)) {
      if (compiled.matcher.exec(target)) return compiled;
    }
  }
  return compilePattern(current.pathname);
}

function getCurrentParams(
  compiled: CompiledPattern,
  ownTokenKeys: Set<string> | null,
  related: CompiledPattern[]
): Record<string, string> {
  const current = currentLocationParts();
  const currentLocation = `${current.pathname}${current.search}${current.hash}`;
  const target = splitUrl(currentLocation);
  const params: Record<string, string> = {};
  const match = compiled.matcher.exec(target);
  if (match) {
    for (const [key, value] of Object.entries(match.pathname.groups)) {
      if (value === undefined || /^\d+$/.test(key)) continue;
      params[key] = value;
    }
  }
  const searchValues = target.search
    ? new URLSearchParams(target.search)
    : null;
  const withinScope = related.some((p) => p.matcher.exec(target));
  for (const { key, token } of compiled.searchTokens) {
    // When navigating to a different route outside this scope, don't carry
    // over search params owned by the destination scope — they belong to that
    // route only and would leak across unrelated lists. Inherited params
    // (e.g. the root-level `modal` params) still persist.
    if (!withinScope && ownTokenKeys?.has(key)) continue;
    const value = searchValues?.get(key);
    if (value !== null && value !== undefined) params[token] = value;
  }
  return params;
}

export function navigate<T extends RouteNode>(
  scope: T | null,
  params?: UrlParams<T> | Record<string, string | number>,
  options?: { replace?: boolean }
) {
  const method = options?.replace ? "replaceState" : "pushState";
  history[method](history.state, "", makeUrl(scope, params));
}

export function useRouteMatch<T extends RouteNode | undefined>(
  scope?: T,
  params?: Record<string, string | number>
): [boolean, Record<string, string>] {
  const location = useLocationState();
  if (!scope) return [true, {}];
  const match = findMatch(collectPatterns(scope), location);
  if (!match) return [false, {}];
  if (params) {
    const paramsMatch = Object.entries(params).every(
      ([key, value]) => match[key] === String(value)
    );
    if (!paramsMatch) return [false, match];
  }
  return [true, match];
}

export const Route: RouteComponent = ({ path, children }: AnyRouteProps) => {
  const location = useLocationState();
  const match = path ? findMatch(collectPatterns(path), location) : null;
  if (!path) return typeof children === "function" ? children() : children;
  if (!match) return null;
  if (typeof children === "function") return children(match);
  return children;
};

export function Switch({ children }: { children: ReactNode }) {
  const location = useLocationState();
  for (const child of Children.toArray(children)) {
    if (!isValidElement<AnyRouteProps>(child) || child.type !== Route) continue;
    const { path } = child.props;
    const match = path ? findMatch(collectPatterns(path), location) : {};
    if (!match) continue;
    const content = child.props.children;
    return typeof content === "function"
      ? path
        ? content(match)
        : (content as () => ReactNode)()
      : content;
  }
  return null;
}

function collectPatterns(scope: RouteNode) {
  const patterns = [compilePattern(scope.pattern)];
  for (const value of Object.values(scope)) {
    if (isRouteNode(value)) {
      patterns.push(...collectPatterns(value));
    }
  }
  return patterns;
}

function findMatch(patterns: CompiledPattern[], location: string) {
  const target = splitUrl(location);
  const searchValues = target.search
    ? new URLSearchParams(target.search)
    : null;
  for (const compiled of patterns) {
    const match = compiled.matcher.exec(target);
    if (!match) continue;
    if (compiled.searchLiterals.some(([k, v]) => searchValues?.get(k) !== v)) {
      continue;
    }
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(match.pathname.groups)) {
      if (value === undefined || /^\d+$/.test(key)) continue;
      params[key] = value;
    }
    for (const { key, token } of compiled.searchTokens) {
      const value = searchValues?.get(key);
      if (value !== null && value !== undefined) params[token] = value;
    }
    return params;
  }
  return null;
}

function buildUrl(params: Record<string, string>, compiled: CompiledPattern) {
  const { pattern } = compiled;
  // Create a map of token -> defaultValue
  const defaults = new Map<string, string>();
  for (const { token, defaultValue } of compiled.searchTokens) {
    if (defaultValue !== undefined) defaults.set(token, defaultValue);
  }

  let url = pattern.replace(/:([A-Za-z0-9_[\]]+)(?:=[^&?]*)?/g, (_, key) => {
    const value = params[key];
    const defaultValue = defaults.get(key);
    // Omit if undefined, empty string, or matches default
    if (value === undefined || value === "" || value === defaultValue)
      return "";
    return encodeURIComponent(value);
  });

  // Clean up empty query params and consecutive separators
  url = url
    .replace(/([?&])[^=]+=(?=&|$)/g, "$1") // Remove empty params
    .replace(/[?&]+/g, (match) => (match.includes("?") ? "?" : "&")) // Collapse consecutive separators
    .replace(/[?&]$/, "") // Remove trailing separator
    .replace(/\?&/, "?"); // Fix ?& to just ?

  return url;
}

function normalizeParams(params?: Record<string, string | number>) {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)])
  );
}

function splitUrl(target: string) {
  const [pathAndSearch = "", hashPart = ""] = target.split("#", 2);
  const [pathnameRaw = "/", searchPart = ""] = pathAndSearch.split("?", 2);
  const pathname = pathnameRaw || "/";
  const search = searchPart ? `?${searchPart}` : undefined;
  const hash = hashPart ? `#${hashPart}` : undefined;
  return { pathname, search, hash };
}

// A query-discriminated child of a route: `?key=<case>` selects a case, whose
// params ride in the standard bracket form `key[param]=value`. Each case is an
// ordinary "k=:k=default" pattern fragment and becomes a page-agnostic route
// scope (pathname `/*`, discriminant as a literal) usable anywhere a route is.
export function oneOf<const C extends Record<string, string>>(cases: C) {
  return { cases } as { cases: C } & {
    [K in keyof C]: RouteDescriptor<`/*?${C[K]}`>;
  };
}
function isOneOf(v: unknown): v is { cases: Record<string, string> } {
  return Boolean(
    v && typeof v === "object" && "cases" in v && !("pattern" in v)
  );
}
type CaseNode = RouteNode & { discriminant: string; caseName: string };
function isCaseNode(v: RouteNode): v is CaseNode {
  return "discriminant" in v;
}
function nsToken(key: string, t: QueryToken, token: string) {
  return `${key}[${t.key}]=:${token}${t.defaultValue === undefined ? "" : `=${t.defaultValue}`}`;
}

export function route<P extends string, C>(pattern: P, children?: C) {
  let folded: string = pattern;
  for (const [key, child] of Object.entries(children ?? {})) {
    if (!isOneOf(child)) continue;
    // Fold the union of case params onto this route (so every page carries
    // them), and give each case its own page-agnostic pattern.
    const union = new Map([[key, `${key}=:${key}`]]);
    for (const [caseName, str] of Object.entries(child.cases)) {
      const tokens = extractTokens(`?${str}`);
      for (const t of tokens) {
        union.set(t.key, nsToken(key, t, `${key}[${t.key}]`));
      }
      const query = tokens.map((t) => nsToken(key, t, t.token)).join("&");
      (child as Record<string, unknown>)[caseName] = {
        pattern: `/*?${key}=${caseName}${query && `&${query}`}`,
        discriminant: key,
        caseName,
      } satisfies CaseNode;
    }
    const q = [...union.values()].join("&");
    folded = folded.includes("?") ? `${folded}&${q}` : `${folded}?${q}`;
  }
  return Object.assign(
    { pattern: folded },
    children ?? {}
  ) as RouteDescriptor<P> & C;
}

function isRouteNode(value: unknown): value is RouteNode {
  return Boolean(
    value &&
    typeof value === "object" &&
    "pattern" in (value as Record<string, unknown>)
  );
}

const appRoutes: RouteNode[] = [];

// makeUrl(null, …) resolves the current URL against every registered tree. The
// app's tree is userland (./routes); that import is circular by design — the
// tree calls the hoisted route()/oneOf() while this module's body runs only
// afterwards.
export function defineRoutes<T extends Record<string, RouteNode>>(config: T) {
  appRoutes.push(...Object.values(config));
  return normalizeRoutes(config) as unknown as NormalizedRoutes<T>;
}

export const routes = defineRoutes(rawRoutes).root;

function normalizeRoutes<T extends Record<string, RouteNode>>(
  config: T,
  base = "",
  baseTail = ""
) {
  for (const value of Object.values(config)) {
    normalizeNode(value, base, baseTail);
  }
  return config;
}

function normalizeNode(node: RouteNode, base: string, baseTail = "") {
  const { path, tail } = splitPattern(node.pattern);
  const absolute = resolvePath(base, path);
  const mergedTail = mergeTails(baseTail, tail);
  node.pattern = `${absolute}${mergedTail}`;
  node.ownTail = tail;
  for (const value of Object.values(node)) {
    if (isRouteNode(value)) normalizeNode(value, absolute, mergedTail);
  }
}

function mergeTails(parentTail: string, childTail: string): string {
  if (!parentTail) return childTail;
  if (!childTail) return parentTail;

  // Extract query and hash parts
  const parentParts = splitPattern(`/${parentTail}`);
  const childParts = splitPattern(`/${childTail}`);

  // Merge query params
  const parentQuery = parentParts.tail.replace(/^[?#]/, "").split("?")[0] || "";
  const childQuery = childParts.tail.replace(/^[?#]/, "").split("?")[0] || "";

  const mergedParams = new Map<string, string>();

  // Add parent params first
  if (parentQuery) {
    for (const segment of parentQuery.split("&")) {
      const eqIndex = segment.indexOf("=");
      if (eqIndex > 0) {
        mergedParams.set(segment.slice(0, eqIndex), segment.slice(eqIndex + 1));
      }
    }
  }

  // Child params override parent params
  if (childQuery) {
    for (const segment of childQuery.split("&")) {
      const eqIndex = segment.indexOf("=");
      if (eqIndex > 0) {
        mergedParams.set(segment.slice(0, eqIndex), segment.slice(eqIndex + 1));
      }
    }
  }

  if (mergedParams.size === 0) return "";

  const queryString = Array.from(mergedParams.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  return queryString ? `?${queryString}` : "";
}

const compiledPatternCache = new Map<string, CompiledPattern>();

function compilePattern(pattern: string): CompiledPattern {
  const cached = compiledPatternCache.get(pattern);
  if (cached) return cached;
  const parts = splitUrl(pattern);
  const compiled: CompiledPattern = {
    pattern,
    matcher: new URLPattern({ pathname: parts.pathname }),
    searchTokens: extractTokens(parts.search),
    searchLiterals: extractLiterals(parts.search),
  };
  compiledPatternCache.set(pattern, compiled);
  return compiled;
}

function extractTokens(part?: string): QueryToken[] {
  if (!part) return [];
  const tokens: QueryToken[] = [];

  for (const segment of part.replace(/^[?#]/, "").split("&")) {
    const eqIndex = segment.indexOf("=");
    if (eqIndex === -1) continue;

    const key = segment.slice(0, eqIndex);
    const value = segment.slice(eqIndex + 1);

    if (!key || key.length === 0 || !value) continue;

    // Parse :token=defaultValue syntax
    const tokenMatch = value.match(/^:([A-Za-z0-9_[\]]+)(?:=(.*))?$/);
    if (!tokenMatch) continue;

    const [, token, defaultValue] = tokenMatch;
    if (!token || token.length === 0) continue;

    tokens.push({ key, token, defaultValue });
  }

  return tokens;
}

function extractLiterals(part?: string): Array<[string, string]> {
  if (!part) return [];
  const literals: Array<[string, string]> = [];
  for (const segment of part.replace(/^[?#]/, "").split("&")) {
    const eqIndex = segment.indexOf("=");
    if (eqIndex <= 0) continue;
    const value = segment.slice(eqIndex + 1);
    if (value && !value.startsWith(":")) {
      literals.push([segment.slice(0, eqIndex), value]);
    }
  }
  return literals;
}

function splitPattern(pattern: string) {
  const [pathAndSearch = "", hashPart = ""] = pattern.split("#", 2);
  const [path = "", queryPart = ""] = pathAndSearch.split("?", 2);
  const tail = `${queryPart ? `?${queryPart}` : ""}${
    hashPart ? `#${hashPart}` : ""
  }`;
  return { path, tail };
}

function resolvePath(base: string, segment: string) {
  if (!segment) return base || "/";
  const trimmed = segment.replace(/^\/+/, "");
  if (!trimmed) return base || "/";
  if (!base || base === "/") return `/${trimmed}`;
  return `${base.replace(/\/+$/, "")}/${trimmed}`;
}

function useLocationState() {
  const subscribe = (listener: () => void) => {
    const events = ["popstate", "hashchange"];
    for (const event of events) addEventListener(event, listener);
    return () => {
      for (const event of events) removeEventListener(event, listener);
    };
  };
  const value = () => `${location.pathname}${location.search}${location.hash}`;
  return useSyncExternalStore(subscribe, value, value);
}

export function useCurrentLocation() {
  return useLocationState();
}

const visitedLocations: string[] = [];

export function lastVisitedLocation(predicate: (url: string) => boolean) {
  return visitedLocations.findLast(predicate);
}

type DeriveHistoryState = (url: string, state: unknown) => unknown;
const stateDerivations: DeriveHistoryState[] = [];

// A module may derive each new history entry's state from the URL being
// navigated to (the modal stack maintains itself this way). Only state carried
// through unchanged is derived; state passed explicitly is trusted as-is.
export function deriveHistoryState(derive: DeriveHistoryState) {
  stateDerivations.push(derive);
}

let navHooksInstalled = false;
export function installBrowserNavigation() {
  if (navHooksInstalled) return;
  navHooksInstalled = true;
  const originals = {
    pushState: history.pushState.bind(history),
    replaceState: history.replaceState.bind(history),
  };
  const commit = (method: "pushState" | "replaceState") =>
    function (state: unknown, title: string, url?: string | URL | null) {
      const current = `${location.pathname}${location.search}${location.hash}`;
      if (visitedLocations.at(-1) !== current) visitedLocations.push(current);
      const target = url == null ? current : String(url);
      const derived =
        state === history.state
          ? stateDerivations.reduce((s, derive) => derive(target, s), state)
          : state;
      // One rule: navigating to the URL you are already on edits the current
      // entry instead of growing history.
      const commitAs =
        method === "pushState" && target !== current
          ? "pushState"
          : "replaceState";
      originals[commitAs](derived, title, url);
      dispatchEvent(new PopStateEvent("popstate"));
    };
  history.pushState = commit("pushState");
  history.replaceState = commit("replaceState");
}

const scrollPositions = new Map<string, number>();

export function useScrollRestoration() {
  const elementRef = useRef<HTMLElement | null>(null);
  const locationRef = useRef<string>(useLocationState());
  const location = locationRef.current;

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const saved = scrollPositions.get(location);
    if (saved) el.scrollTop = saved;

    return () => {
      if (el.scrollTop) scrollPositions.set(location, el.scrollTop);
    };
  }, [location]);

  return (node: HTMLElement | null) => {
    elementRef.current = node;
  };
}
